import { box, type Aabb, type World } from './sim.js';
import type { Vec3 } from './math.js';
// Type-only, and so erased under `verbatimModuleSyntax`: `quests.ts` imports `MapDef` from here,
// and a *value* import in this direction would close a runtime cycle. `VoiceId` is the join
// between a quest chain's speaker and the body standing on the ground — see `NpcDef.voice`.
import type { VoiceId } from './quests.js';
import { SPINE } from './maps/spine.js';
import { LANTERNFALL } from './maps/lanternfall.js';
import { VIGIL_DOWNS } from './maps/vigil-downs.js';

export interface LootContainerDef {
  id: number;
  pos: Vec3;
  /** Key into LOOT_TABLES. */
  table: string;
}
export interface ExtractDef {
  id: number;
  name: string;
  pos: Vec3;
  radius: number;
}
export interface HymnSpawnDef {
  /** EntityKind value. */
  kind: number;
  pos: Vec3;
  /** Patrol route (waypoint indices) or empty for a sentry. */
  patrol: number[];
}
export interface WaypointGraph {
  nodes: Vec3[];
  edges: [number, number][];
}
/**
 * A named region an Expedition quest can be pinned to (PURGE / HOLD / a placed HUNT). Cylindrical:
 * `radius` is the XZ radius and `halfHeight` the vertical reach either side of `pos.y`, so the
 * Spine's stacked decks (6 m apart) stay distinct regions. See `quests.ts` / `inArea`.
 */
export interface AreaDef {
  id: number;
  /** Reads inside an objective line: "Kill 6 Choristers in <name>". */
  name: string;
  pos: Vec3;
  radius: number;
  halfHeight: number;
}

/**
 * Somebody standing on the map. Static, authored, and never an entity.
 *
 * An NPC is map data rather than a snapshot entity on purpose: the server spawns nothing for
 * one, has no AI, no HP and no navmesh presence to give it, and the client draws it straight
 * out of this definition. The only thing that ever crosses the wire is the player's
 * `InteractAction.Talk` and an id — no coordinate — so the server validates the reach against
 * *this* position, and "never trust a client position" is structural rather than checked.
 */
export interface NpcDef {
  id: number;
  /** Nameplate, first line. */
  name: string;
  /** Nameplate, second line: who they are to the town, in a word or two. */
  role: string;
  /** Which character GLB gives them a body — the client's `CharacterId`. */
  body: string;
  pos: Vec3;
  /** Facing, in radians. yaw 0 faces -Z and positive yaw turns left (see `sim.ts`). */
  yaw: number;
  /** Said once, in their own voice, the first time a player comes within `NPC_BARK_RANGE`. */
  bark: string;
  /**
   * Uniform scale on the body only — never the lantern, and never the nameplate's anchor beyond
   * what the head needs. Every character GLB is normalised to `PLAYER_HEIGHT` by the client's
   * rig, so a person who is not adult-sized has to say so here. Absent means 1.
   */
  bodyScale?: number;
  /**
   * The quest voice this body *is* (`quests.ts`'s `VoiceId`). Set means a finished authored rung
   * spoken by this voice does not advance on its own on this map: the chain waits, the tracker
   * says to come back, and `InteractAction.Talk` on this person is what turns it in and pays.
   *
   * Absent means the voice is only a radio here — a chain whose speaker has no body on the map
   * (Ferryman Wren, The Choir) keeps auto-advancing, because there is nobody to walk to. The
   * server reads this in `expedition.ts`; nothing about it crosses the wire but the id.
   */
  voice?: VoiceId;
  /**
   * The extract this person *is*. Set means talking to them leaves the Band through that
   * `ExtractDef` — see `room.ts`'s `handleTalk`. Absent means they are only somebody to meet.
   */
  extractId?: number;
  /**
   * True for a body that keeps a counter: talking to them opens the market panel, and the server
   * will only take Wick for a repair (`InteractAction.Repair`) from somebody standing at one.
   *
   * Deliberately a map-data flag rather than a protocol message. The client already has every
   * `NpcDef` at boot, so it can decide locally to open a panel; the server reads the same field
   * for the reach test, so the prompt and the thing that grants it cannot drift apart. Today
   * Sabel Roque in Lanternfall is the only one (`vendor.ts`'s `SABEL_NPC_ID`).
   */
  vendor?: boolean;
}

/**
 * How close a player has to be to talk. One number, read by the client's interact scan and by
 * the server's validation, so the prompt and the thing that grants it can never drift apart.
 */
export const NPC_TALK_RANGE = 3;
/** First-approach bark radius. Client-side flavour only; nothing about it is authoritative. */
export const NPC_BARK_RANGE = 6;

/**
 * A seamless zone boundary. Standing inside `region` hands the player to `to` in the same
 * process: the server moves them between rooms and sends `S2C.Transfer`, and the client swaps
 * the map it already prefetched. There is no reconnect and no load screen — see
 * `docs/PROTOCOL-V3.md` §3.10.
 */
export interface ExitDef {
  id: number;
  /** Reads in the prompt: "To <name>". */
  name: string;
  /** Target `MapDef.id`. */
  to: string;
  /** Cylindrical trigger, same shape as `AreaDef`. */
  region: { pos: Vec3; radius: number; halfHeight: number };
  /** Where you arrive in the target map, and which way you face (radians, yaw 0 faces -Z). */
  spawn: Vec3;
  yaw: number;
}

export interface MapDef {
  id: string;
  name: string;
  world: World;
  spawns: Vec3[];
  /**
   * A city or a zone that is simply always there: its room is never reaped, never Ended, and
   * character state is written back to SQLite rather than settled at an extract. Absent means
   * the old one-shot room (a tutorial yard, a test map).
   */
  persistent?: boolean;
  /** Population cap. Default: `PERSISTENT_MAX_PLAYERS` when persistent, else `MAX_PLAYERS_PER_ROOM`. */
  maxPlayers?: number;
  /** Release points. Death sends you to the nearest one; the first is the map's default. */
  graveyards?: Vec3[];
  /** Seamless boundaries out of this map. */
  exits?: ExitDef[];
  /**
   * The zone's danger gradient: `[min, max]` monster level, laid out by distance from `spawns[0]`
   * (the gate). A camp at the gate spawns at `min`, the furthest camp at `max`, and everything
   * between is banded in order — see `levelForDistance` in `apps/server/src/game/ai/tuning.ts`.
   *
   * **Absent means every monster on this map is level 1**, which is exactly the behaviour every
   * map had before levels existed. That default is deliberate rather than lazy: a test yard, the
   * Spine and The Waking have no gradient to express, and inferring one from wherever their
   * handful of Hymns happen to stand would silently turn a fixture into a level-9 fight.
   */
  levelRange?: [number, number];
  /**
   * What `{area}` reads as in an objective whose `areaHint` names ground this map does not have
   * (the quest library is shared across maps). Falls back to `areas[0].name`.
   */
  defaultAreaName?: string;
  /** Raid content; optional so test yards stay minimal. */
  containers?: LootContainerDef[];
  extracts?: ExtractDef[];
  hymnSpawns?: HymnSpawnDef[];
  waypoints?: WaypointGraph;
  /** Named regions Expedition quests can be pinned to. Maps without any never roll PURGE/HOLD. */
  areas?: AreaDef[];
  /** The people standing on this ground. Drawn from the map file; never in a snapshot. */
  npcs?: NpcDef[];
  /** Raid length in ms; Dimming starts at the last 3 minutes. */
  raidDurationMs?: number;
  /**
   * The subset of `world.boxes` whose visuals come from a region GLB rather than from the box
   * itself (see maps/region.ts): the client must not draw these as greybox crates when it has
   * the GLB. Absent when the map has no region applied.
   */
  regionBoxes?: readonly Aabb[];
  /**
   * The deck-kit boxes (from `world.boxes`) whose visuals the wreck GLB carries: the client
   * skips these in its greybox batches when it has that GLB, and still greyboxes any kit box
   * that is *not* listed (a box added to spine.ts after the last Blender export). Absent
   * without a region.
   */
  wreckDressedBoxes?: readonly Aabb[];
}

/**
 * The Yard: the M0 greybox pad, promoted to the starter zone.
 *
 * It is still the developer test map (`?debug` deploys here) — the geometry has not moved a
 * centimetre. What it has now is the four things a tutorial needs and nothing more: one extract
 * (The Ferry, at the gate the spawns face), one sealed crate on the open floor, three Chorister
 * posts on the far side of the low wall, and three named areas The Waking's REACH steps aim at.
 *
 * Deliberately small. `director OFF` in tutorial mode means the only Hymns in here are those
 * three, so the yard must be legible in one look from the spawn.
 */
export const YARD: MapDef = {
  id: 'yard',
  name: 'Lanternfall Yard',
  world: {
    groundY: 0,
    boxes: [
      box(6, 1, -8, 4, 2, 4),
      box(-7, 0.75, -4, 3, 1.5, 3),
      box(-7, 2.25, -4, 1.5, 1.5, 1.5),
      box(0, 1.5, -18, 12, 3, 1),
      box(12, 2, 4, 1, 4, 10),
      box(-14, 2, 6, 1, 4, 14),
      box(3, 0.5, 6, 2, 1, 2),
      box(3, 1.5, 6, 1, 1, 1),
    ],
  },
  spawns: [
    { x: 0, y: 0, z: 4 },
    { x: 4, y: 0, z: 2 },
    { x: -4, y: 0, z: 2 },
    // Was { x: 0, y: 0, z: -4 }, which sits 2 m from the Waking Platform area (centre
    // z = -2, radius 3): a player dealt that spawn stood inside the first REACH step and
    // cleared it before moving, so The Waking opened by asking for a thing already done.
    { x: 0, y: 0, z: 8 },
    { x: 8, y: 0, z: -2 },
    { x: -8, y: 0, z: 0 },
    { x: 2, y: 0, z: -10 },
    { x: -3, y: 0, z: -12 },
    { x: 9, y: 0, z: 8 },
    { x: -10, y: 0, z: 10 },
    { x: 5, y: 0, z: 12 },
    { x: -2, y: 0, z: 14 },
  ],
  /** The gate. One way out, straight ahead of every spawn, so "walk to the light" is findable. */
  extracts: [{ id: 1, name: 'The Ferry', pos: { x: 0, y: 0, z: 17 }, radius: 3 }],
  /** One sealed Compact crate, on the open floor between the spawns and the low wall. */
  containers: [{ id: 1, pos: { x: 4.5, y: 0, z: -5.5 }, table: 'yard_crate' }],
  /**
   * Isolde Vane — the first person in STILLSUN who is a body and not a voice.
   *
   * Two things about where she stands are load-bearing.
   *
   * **Beside the pad, not on it.** 3.26 m from the Ferry's centre against a radius of 3, so the
   * walkable middle of the extract stays clear and she reads as somebody waiting at the gate
   * rather than as the gate's furniture.
   *
   * **Facing the yard.** yaw 0 faces -Z, so a facing is `atan2(-dx, -dz)` toward what it should
   * look at; toward the middle of the approach (0, 8) that is 0.36 rad. Every spawn is at
   * greater -Z than she is, so this is the whole difference between meeting a woman and walking
   * up behind one.
   *
   * She is the way out of The Waking: `extractId` links her to The Ferry, and in tutorial mode
   * the beacon's positional hold is off — you leave by talking to her. See `room.ts`.
   */
  npcs: [
    {
      id: 1,
      name: 'Isolde Vane',
      role: 'Lanternfall',
      // char-04 (Erika Archer): hooded, layered cloth over a sash — the settler-canvas
      // silhouette in the library, and the only one that does not read as somebody kitted to
      // go out there. Her hips rest at 1.042 against the 1.061 every clip was baked on, so the
      // idle sits on her almost untouched.
      body: 'char-04',
      pos: { x: 3.2, y: 0, z: 16.4 },
      yaw: 0.36,
      bark: 'That is close enough to see you by. Come the rest of the way and I will light the thirty-second.',
      extractId: 1,
    },
  ],
  /** Three Choristers, posted behind the low wall at z = -18 so nothing is in the spawn's face. */
  hymnSpawns: [
    { kind: 1, pos: { x: -3, y: 0, z: -22 }, patrol: [] },
    { kind: 1, pos: { x: 1.5, y: 0, z: -23.5 }, patrol: [] },
    { kind: 1, pos: { x: 5, y: 0, z: -21.5 }, patrol: [] },
  ],
  /**
   * Named ground for The Waking's REACH steps. The Stepped Platform sits at y = 3 — the top of
   * the two-crate stack at x = -7 — with a half-height tight enough that standing on the *lower*
   * crate (y = 1.5) does not count: the step is "get all the way up", and it has to mean it.
   */
  areas: [
    { id: 1, name: 'the Waking Platform', pos: { x: 0, y: 0, z: -2 }, radius: 3, halfHeight: 2 },
    {
      id: 2,
      name: 'the Stepped Platform',
      pos: { x: -7, y: 3, z: -4 },
      radius: 1.8,
      halfHeight: 1,
    },
    { id: 3, name: 'the Ferry', pos: { x: 0, y: 0, z: 17 }, radius: 3, halfHeight: 3 },
  ],
  /** A minimal graph so Hymns can walk the yard and the debug expedition has somewhere to spawn. */
  waypoints: {
    nodes: [
      { x: 0, y: 0, z: 12 },
      { x: 0, y: 0, z: 2 },
      { x: -8, y: 0, z: 0 },
      { x: 8, y: 0, z: -4 },
      { x: 0, y: 0, z: -12 },
      { x: -6, y: 0, z: -22 },
      { x: 6, y: 0, z: -22 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [1, 3],
      [1, 4],
      [2, 4],
      [3, 4],
      [4, 5],
      [4, 6],
      [5, 6],
    ],
  },
};

/**
 * Every map the server can open a room on.
 *
 * `lanternfall` and `vigil-downs` are the persistent world and the only two a player reaches
 * normally. `yard` and `spine` are kept but are no longer default ground: the Yard still runs The
 * Waking, and the Spine is the one place the old one-shot room, its quest director and its loot
 * economy still live (see `Room.director`). Neither is persistent, so neither saves a character's
 * position, and `DEFAULT_MAP_ID` points at the city.
 */
export const MAPS: Record<string, MapDef> = {
  [LANTERNFALL.id]: LANTERNFALL,
  [VIGIL_DOWNS.id]: VIGIL_DOWNS,
  [YARD.id]: YARD,
  [SPINE.id]: SPINE,
};

/** The Verger's arena on the Spine (Choir Deck floor); the server AI spawns the boss here. */
export { SPINE_VERGER_SPAWN } from './maps/spine.js';
